import React, { FunctionComponent, JSX, SyntheticEvent, useRef, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import styles from './headerPlanningMobile.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { CalendarView } from '../../utils/calendarParameters'
import { useTranslation } from 'react-i18next'
import { Badge, Divider, Menu } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import FilterMobileDialog from '../../Dialog/filterMobile/filterMobileDialog'
import SelectDaycareMobileDialog from '../../Dialog/selectDaycareMobileDialog/selectDaycareMobileDialog'
import { Parent } from '@/types/Parent'
import KPIMobileDialog from '../../Dialog/KPIMobileDialog/KPIMobileDialog'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import {
    AllInclusive,
    ChildCare,
    ContactPage,
    FilterAlt,
    GridView,
    Groups,
    MoreHoriz,
    Person,
    Refresh
} from '@mui/icons-material'
import { changeTabAndResetFilters, parentHaveChildRequest } from '../headerHelper.ts'
import { PlanningRights, PlanningTab, PlanningTabId, PlanningTabsState } from '@/hooks/usePlanningRights.ts'
import { PlanningKPIData } from '../../hooks/usePlanningKPI.ts'

interface HeaderPlanningMobileProps {
    currentDaycare: Daycare
    planningRights: PlanningRights
    planningTabs: PlanningTabsState
    currentView: string
    setCurrentView: (view: string) => void
    parent?: Parent
    refetchData: (isGlobalPlanning?: boolean) => void
    handleAddSlot: () => void
    haveScheduledSlots: boolean
    setSelectFilters: (filters: string[]) => void
    selectFilters: string[]
    filterEvents: (filters: string[]) => void
    selectFiltersOptions: SelectOptionsDeprecated
    kpi: PlanningKPIData
    requestNumber: number
    setScheduledSlotsDialogOpen: (open: boolean) => void
    setRequestSlotDialogOpen: (open: boolean) => void
}

enum OtherViewOptions {
    myDaycares = 'myDaycares',
    liberatedSlots = 'liberatedSlots',
    requestSlot = 'requestSlot',
    addOccasionalSlot = 'addOccasionalSlot',
    myRequests = 'myRequests',
    myIndicators = 'myIndicators'
}

const HeaderPlanningMobile: FunctionComponent<HeaderPlanningMobileProps> = ({
    currentDaycare,
    planningTabs,
    planningRights,
    currentView,
    setCurrentView,
    setSelectFilters,
    parent,
    refetchData,
    handleAddSlot,
    haveScheduledSlots,
    selectFilters,
    filterEvents,
    selectFiltersOptions,
    kpi,
    requestNumber,
    setScheduledSlotsDialogOpen,
    setRequestSlotDialogOpen
}: HeaderPlanningMobileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [isFilterDialogOpen, setFilterDialogOpen] = useState<boolean>(false)
    const [isSelectDaycareDialogOpen, setSelectDaycareDialogOpen] = useState<boolean>(false)
    const [isKPIDialogOpen, setKPIDialogOpen] = useState<boolean>(false)

    const haveChildRequest: boolean = parentHaveChildRequest(currentDaycare.id, parent)

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
    const menuCalendarView: boolean = Boolean(menuAnchorEl)

    const calendarViewOptions: SelectOptionDeprecated[] = [
        { value: CalendarView.Agenda, name: t('agenda') },
        { value: CalendarView.Current, name: t('current') },
        { value: CalendarView.OneWeek, name: t('week') },
        { value: CalendarView.OneMonth, name: t('months') }
    ]

    const [otherAnchorEl, setOtherAnchorEl] = useState<null | HTMLElement>(null)
    const otherMenuOpen: boolean = Boolean(otherAnchorEl)

    const viewMenuRef: React.RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)
    const otherMenuRef: React.RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)

    const otherViewOptions: SelectOptionDeprecated[] = []

    if (haveScheduledSlots && planningRights.canWriteOccasionalSlot) {
        otherViewOptions.push({ value: OtherViewOptions.liberatedSlots, name: t('liberatedSlots') })
    }
    if (haveChildRequest) {
        otherViewOptions.push({ value: OtherViewOptions.requestSlot, name: t('requestSlot') })
    }
    if (!planningRights.isParent && planningRights.canWriteOccasionalSlot) {
        otherViewOptions.push({ value: OtherViewOptions.addOccasionalSlot, name: t('addOccasionalSlot') })
    }
    if (!planningRights.isParent && (planningRights.canReadOccasionalSlot || planningRights.canReadContracts)) {
        otherViewOptions.push({ value: OtherViewOptions.myRequests, name: t('myRequests') })
    }

    const showDivider: boolean = haveChildRequest || (!planningRights.isParent && planningRights.canWriteOccasionalSlot)

    otherViewOptions.push({ value: OtherViewOptions.myIndicators, name: t('myIndicators') })

    if (user && user.daycares.length > 1 && !planningRights.isParent) {
        otherViewOptions.push({ value: OtherViewOptions.myDaycares, name: t('myDaycares') })
    }

    function handleOtherMenu(select: string): void {
        setOtherAnchorEl(null)

        switch (select) {
            case OtherViewOptions.liberatedSlots:
                setScheduledSlotsDialogOpen(true)
                break
            case OtherViewOptions.requestSlot:
                setRequestSlotDialogOpen(true)
                break
            case OtherViewOptions.addOccasionalSlot:
                handleAddSlot()
                break
            case OtherViewOptions.myRequests:
                navigate(`/request?tab=${planningTabs.activeTabId}`)
                break
            case OtherViewOptions.myDaycares:
                setSelectDaycareDialogOpen(true)
                break
            case OtherViewOptions.myIndicators:
                setKPIDialogOpen(true)
                break
            default:
                break
        }
    }

    function handleSelectView(view: string): void {
        setCurrentView(view)
        setMenuAnchorEl(null)
    }

    return (
        <div className={`${styles.HeaderPosition} ${planningRights.isParent ? styles.HeaderPositionParent : ''}`}>
            <div className={styles.LeftContainer}>
                <ButtonIcon
                    icon={<Refresh className={styles.Icon} />}
                    onClick={() => refetchData(planningTabs.isGlobalTab)}
                    className={styles.Button}
                    parent={planningRights.isParent}
                />
                {planningTabs.isStaffTab && planningRights.canReadStaffRights ? (
                    <ButtonIcon
                        icon={<Groups className={styles.Icon} />}
                        className={styles.Button}
                        onClick={() => navigate('/staff')}
                    />
                ) : (
                    !planningTabs.isStaffTab &&
                    (planningRights.canReadOccasionalSlot || planningRights.canReadContracts) && (
                        <div ref={viewMenuRef}>
                            <ButtonIcon
                                icon={<GridView className={styles.Icon} />}
                                className={styles.Button}
                                parent={planningRights.isParent}
                                onClick={() => setMenuAnchorEl(viewMenuRef.current)}
                            />
                        </div>
                    )
                )}
                <Menu
                    open={menuCalendarView}
                    onClose={() => setMenuAnchorEl(null)}
                    anchorEl={menuAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    style={{ marginTop: 10 }}
                >
                    {calendarViewOptions.map((option: SelectOptionDeprecated) => (
                        <MenuItem
                            key={option.value}
                            onClick={() => handleSelectView(option.value)}
                            disabled={currentView === option.value}
                        >
                            {option.name}
                        </MenuItem>
                    ))}
                </Menu>
            </div>
            <div className={styles.CenterContainer}>
                {!parent && planningTabs.tabs.length > 1 && (
                    <div className={styles.Tabs}>
                        {planningTabs.tabs.map((tab: PlanningTab, index: number) =>
                            getIconForTab(
                                tab,
                                index,
                                planningTabs.activeIndex,
                                (event, newValue) =>
                                    changeTabAndResetFilters(
                                        planningTabs,
                                        selectFilters,
                                        setSelectFilters,
                                        event,
                                        newValue
                                    ),
                                t
                            )
                        )}
                    </div>
                )}
                {planningRights.isParent && parent && user && user.daycares.length > 1 && (
                    <DaycareSelector removeBackground={true} />
                )}
            </div>
            <div className={styles.RightContainer}>
                {(planningRights.canReadOccasionalSlot ||
                    planningRights.canReadContracts ||
                    planningRights.isParent) && (
                    <ButtonIcon
                        icon={<FilterAlt className={styles.Icon} />}
                        onClick={() => setFilterDialogOpen(true)}
                        className={styles.Button}
                        parent={planningRights.isParent}
                    />
                )}
                <div ref={otherMenuRef}>
                    <ButtonIcon
                        icon={<MoreHoriz className={styles.Icon} />}
                        className={styles.Button}
                        parent={planningRights.isParent}
                        badge={requestNumber}
                        onClick={() => setOtherAnchorEl(otherMenuRef.current)}
                    />
                </div>
                <Menu
                    open={otherMenuOpen}
                    onClose={() => setOtherAnchorEl(null)}
                    anchorEl={otherAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    style={{ marginTop: 10 }}
                >
                    {otherViewOptions.map((option: SelectOptionDeprecated) =>
                        option.value === OtherViewOptions.myRequests ? (
                            [
                                showDivider && <Divider key={`divider-${option.value}`} />,
                                <Badge key={option.value} badgeContent={requestNumber} className={styles.BadgeDaycare}>
                                    <MenuItem onClick={() => handleOtherMenu(option.value)}>{option.name}</MenuItem>
                                </Badge>
                            ]
                        ) : (
                            <MenuItem key={option.value} onClick={() => handleOtherMenu(option.value)}>
                                {option.name}
                            </MenuItem>
                        )
                    )}
                </Menu>
            </div>
            {isSelectDaycareDialogOpen && (
                <SelectDaycareMobileDialog
                    open={isSelectDaycareDialogOpen}
                    handleClose={() => setSelectDaycareDialogOpen(false)}
                    isParent={planningRights.isParent}
                />
            )}
            {isFilterDialogOpen && (
                <FilterMobileDialog
                    open={isFilterDialogOpen}
                    handleClose={() => setFilterDialogOpen(false)}
                    selectFilters={selectFilters}
                    filterEvents={filterEvents}
                    selectFiltersOptions={selectFiltersOptions}
                    isParent={planningRights.isParent}
                />
            )}
            {isKPIDialogOpen && (
                <KPIMobileDialog
                    open={isKPIDialogOpen}
                    planningTabs={planningTabs}
                    handleClose={() => setKPIDialogOpen(false)}
                    isParent={planningRights.isParent}
                    kpi={kpi}
                    filterEvents={filterEvents}
                    merger={currentDaycare.merger}
                />
            )}
        </div>
    )
}

export default HeaderPlanningMobile

function getIconForTab(
    tab: PlanningTab,
    index: number,
    tabsValue: number,
    handleTabChange: (e: SyntheticEvent, newValue: number) => void,
    t: (t: string) => string
): JSX.Element {
    switch (tab.id) {
        case PlanningTabId.REGULAR_TAB:
            return (
                <ContactPage
                    key={index}
                    className={`${styles.Tab} 
							${index === tabsValue ? styles.TabActive : ''}`}
                    onClick={(e) => handleTabChange(e, index)}
                />
            )
        case PlanningTabId.OCCASIONAL_TAB:
            return (
                <ChildCare
                    key={index}
                    className={`${styles.Tab} 
							${index === tabsValue ? styles.TabActive : ''}`}
                    onClick={(e) => handleTabChange(e, index)}
                />
            )
        case PlanningTabId.STAFF_TAB:
            return (
                <Person
                    key={index}
                    className={`${styles.Tab} 
							${index === tabsValue ? styles.TabActive : ''}`}
                    onClick={(e) => handleTabChange(e, index)}
                />
            )
        case PlanningTabId.GLOBAL_TAB:
            return (
                <AllInclusive
                    key={index}
                    className={`${styles.Tab} 
							${index === tabsValue ? styles.TabActive : ''}`}
                    onClick={(e) => handleTabChange(e, index)}
                />
            )
        default:
            return (
                <a
                    key={index}
                    className={`${styles.Tab} 
							${index === tabsValue ? styles.TabActive : ''}`}
                    onClick={(e) => handleTabChange(e, index)}
                >
                    {t(tab.label)}
                </a>
            )
    }
}
